import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight, Search, User, Sparkles, CalendarDays } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
export type BlogPost = {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
};
export const blogPosts: BlogPost[] = [{
  id: 'fsai-enforcement-update-2025',
  title: 'FSAI Enforcement Update: What Changed for Irish Food Businesses in 2025',
  excerpt: "A breakdown of the latest FSAI enforcement actions, closure orders, and what they mean for compliance standards across Ireland's food industry.",
  category: 'News',
  author: 'Sarah Murphy',
  date: '22 Apr 2025',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  featured: true
}, {
  id: 'allergen-regulation-changes-ireland',
  title: 'Allergen Labelling Regulations: 2025 Changes Irish Businesses Need to Know',
  excerpt: "Updated allergen labelling requirements are now in effect across Ireland. Here's what has changed and how to ensure your business is compliant.",
  category: 'Compliance',
  author: "James O'Brien",
  date: '15 Apr 2025',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 'haccp-compliance-checklist',
  title: 'HACCP Compliance Checklist: Preparing for Your Next EHO Inspection',
  excerpt: 'A step-by-step compliance checklist covering everything Environmental Health Officers look for during routine food safety inspections in Ireland.',
  category: 'Guides',
  author: 'Sarah Murphy',
  date: '8 Apr 2025',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 'food-safety-closures-q1-2025',
  title: 'Food Safety Closure Orders in Ireland: Q1 2025 Report',
  excerpt: 'An analysis of closure orders and improvement notices issued by the FSAI in Q1 2025, including the most common violations found.',
  category: 'News',
  author: 'Ciara Kelly',
  date: '1 Apr 2025',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 'temperature-monitoring-guide',
  title: 'Temperature Monitoring: A Practical Guide for Irish Food Businesses',
  excerpt: 'How to implement a compliant temperature monitoring system that meets FSAI requirements, including recording schedules and corrective actions.',
  category: 'Guides',
  author: "James O'Brien",
  date: '25 Mar 2025',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1567521464027-f127ff144326?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 'new-staff-training-requirements',
  title: 'FSAI Training Requirements: What New Staff Must Complete Before Handling Food',
  excerpt: 'A compliance overview of the FSAI training guidelines for new employees, including Level 1 and Level 2 timeframes and documentation requirements.',
  category: 'Compliance',
  author: 'Ciara Kelly',
  date: '18 Mar 2025',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const categories = ['All', 'News', 'Compliance', 'Guides'];
export function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const featured = blogPosts.find(p => p.featured);
  const regularPosts = blogPosts.filter(p => !p.featured);
  const filteredPosts = regularPosts.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="min-h-screen bg-cream-50 pt-20 pb-24" data-id="element-141">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 text-white overflow-hidden py-20 mb-16" data-id="element-142">
        <div className="absolute inset-0 bg-mesh opacity-30" data-id="element-143"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ember-500/15 rounded-full blur-3xl" data-id="element-144"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-145">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-146">
            <Badge variant="gradient" className="mb-6" data-id="element-147">
              <Sparkles className="w-3 h-3 mr-1" data-id="element-148" /> Insights & Guides
            </Badge>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-149">
              Food Safety <span className="text-ember-400" data-id="element-150">Blog</span>
            </h1>
            <p className="text-xl text-forest-100 max-w-2xl mx-auto leading-relaxed" data-id="element-151">
              Compliance updates, practical guides, and food safety news to help
              you maintain the highest food safety standards.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-152">
        {/* Featured Post */}
        {featured && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-16" data-id="element-153">
            <Link to={`/blog/${featured.id}`} className="group block" data-id="element-154">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden border-2 border-forest-100 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" data-id="element-155">
                <div className="relative h-64 lg:h-auto overflow-hidden" data-id="element-156">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-157" />
                  <div className="absolute top-4 left-4" data-id="element-158">
                    <Badge variant="warning" data-id="element-159">Featured</Badge>
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center" data-id="element-160">
                  <Badge variant="outline" className="mb-4 self-start" data-id="element-161">
                    {featured.category}
                  </Badge>
                  <h2 className="text-2xl lg:text-3xl font-display font-bold text-forest-900 mb-4 group-hover:text-forest-700 transition-colors" data-id="element-162">
                    {featured.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-6" data-id="element-163">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-164">
                    <div className="flex items-center text-sm text-gray-500" data-id="element-165">
                      <User className="w-4 h-4 mr-1" data-id="element-166" /> {featured.author}
                      <span className="mx-2" data-id="element-167">·</span>
                      <CalendarDays className="w-4 h-4 mr-1" data-id="element-168" /> {featured.date}
                      <span className="mx-2" data-id="element-169">·</span>
                      <Clock className="w-4 h-4 mr-1" data-id="element-170" /> {featured.readTime}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>}

        {/* Filters */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12" data-id="element-171">
          <div className="flex flex-wrap gap-3" data-id="element-172">
            {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${activeCategory === cat ? 'bg-forest-700 text-white shadow-md' : 'bg-white text-gray-600 border-2 border-forest-100 hover:border-forest-300'}`} data-id="element-173">
                {cat}
              </button>)}
          </div>
          <div className="relative w-full lg:w-80" data-id="element-174">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-175" />
            <input type="text" placeholder="Search articles..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-forest-100 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-sm font-medium bg-white shadow-sm" data-id="element-176" />
          </div>
        </div>

        {/* Post Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-177">
          {filteredPosts.map((post, i) => <motion.div key={post.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.08
        }} data-id="element-178">
              <Link to={`/blog/${post.id}`} className="group block h-full" data-id="element-179">
                <article className="bg-white rounded-3xl overflow-hidden border-2 border-forest-100 hover:border-forest-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col" data-id="element-180">
                  <div className="relative h-48 overflow-hidden" data-id="element-181">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-182" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col" data-id="element-183">
                    <Badge variant="outline" className="mb-3 self-start text-[10px]" data-id="element-184">
                      {post.category}
                    </Badge>
                    <h3 className="text-xl font-display font-bold text-forest-900 mb-3 group-hover:text-forest-700 transition-colors leading-tight" data-id="element-185">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1 line-clamp-3" data-id="element-186">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-forest-50" data-id="element-187">
                      <span className="flex items-center" data-id="element-188">
                        <User className="w-3 h-3 mr-1" data-id="element-189" /> {post.author}
                      </span>
                      <span className="flex items-center" data-id="element-190">
                        <Clock className="w-3 h-3 mr-1" data-id="element-191" /> {post.readTime}
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            </motion.div>)}
        </div>

        {filteredPosts.length === 0 && <div className="text-center py-20" data-id="element-192">
            <p className="text-xl text-gray-500 font-medium" data-id="element-193">
              No articles found matching your search.
            </p>
          </div>}
      </div>
    </div>;
}